// resources/js/src/store/storyStore.ts

import { create } from "zustand";
import { StoryApi } from "../services/story.api";

import { ReactionType } from "../components/community/components/post/interfaces/types";
import { CreateStoryPayload, Story, StoryGroup } from "../components/community/components/stories/interfaces/storyTypes";

interface StoryState {
  groups: StoryGroup[];
  isLoading: boolean;
  isSaving: boolean;
  error: string;

  loadStories: () => Promise<void>;
  createStory: (payload: CreateStoryPayload) => Promise<boolean>;
  deleteStory: (storyId: number) => Promise<boolean>;
  markSeen: (storyId: number) => Promise<boolean>;
  reactToStory: (storyId: number, reaction: ReactionType) => Promise<boolean>;
  replyToStory: (storyId: number, text: string) => Promise<boolean>;
}

/** helper: عدّل ستوري واحدة داخل مجموعتها */
function updateStoryInGroups(
  groups: StoryGroup[],
  storyId: number,
  updater: (s: Story) => Story
): StoryGroup[] {
  return groups.map((group) => {
    const idx = group.stories.findIndex((s) => s.id === storyId);
    if (idx === -1) return group;

    const newStories = group.stories.map((s, i) =>
      i === idx ? updater(s) : s
    );

    return {
      ...group,
      stories: newStories,
      allSeen: newStories.every((s) => s.seen),
    };
  });
}

/** helper: احذف ستوري من مجموعتها، وامسح المجموعة لو فضيت */
function removeStoryFromGroups(
  groups: StoryGroup[],
  storyId: number
): StoryGroup[] {
  return groups
    .map((group) => {
      const filtered = group.stories.filter((s) => s.id !== storyId);
      if (filtered.length === group.stories.length) return group;

      return {
        ...group,
        stories: filtered,
        allSeen: filtered.every((s) => s.seen),
      };
    })
    .filter((g) => g.stories.length > 0);
}

export const useStoryStore = create<StoryState>((set, get) => ({
  groups: [],
  isLoading: false,
  isSaving: false,
  error: "",

  loadStories: async () => {
    set({ isLoading: true, error: "" });
    try {
      const data = await StoryApi.list();
      set({
        groups: Array.isArray(data) ? data : [],
        isLoading: false,
      });
    } catch (e) {
      set({
        groups: [],
        isLoading: false,
        error: e instanceof Error ? e.message : "Failed to load stories",
      });
    }
  },

  createStory: async (payload) => {
    set({ isSaving: true, error: "" });
    try {
      const created = await StoryApi.create(payload);

      // ضيف الستوري الجديدة لمجموعة صاحبها، أو أنشئ مجموعة جديدة
      set((s) => {
        const groups = [...s.groups];
        const existingIdx = groups.findIndex(
          (g) => g.userId === created.author.id
        );

        if (existingIdx === -1) {
          groups.unshift({
            userId: created.author.id,
            author: created.author,
            allSeen: created.seen,
            latestAt: created.createdAt,
            stories: [created],
          });
        } else {
          const g = groups[existingIdx];
          const updatedStories = [...g.stories, created];
          groups[existingIdx] = {
            ...g,
            stories: updatedStories,
            allSeen: updatedStories.every((st) => st.seen),
            latestAt: created.createdAt,
          };
        }

        return { groups, isSaving: false };
      });
      return true;
    } catch (e) {
      set({
        isSaving: false,
        error: e instanceof Error ? e.message : "Failed to create story",
      });
      return false;
    }
  },

  deleteStory: async (storyId) => {
    const before = get().groups;
    set({ groups: removeStoryFromGroups(before, storyId) });
    try {
      await StoryApi.remove(storyId);
      return true;
    } catch (e) {
      set({
        groups: before,
        error: e instanceof Error ? e.message : "Failed to delete story",
      });
      return false;
    }
  },

  markSeen: async (storyId) => {
    const before = get().groups;
    set({
      groups: updateStoryInGroups(before, storyId, (s) => ({
        ...s,
        seen: true,
      })),
    });
    try {
      await StoryApi.markSeen(storyId);
      return true;
    } catch {
      set({ groups: before });
      return false;
    }
  },

  reactToStory: async (storyId, reaction) => {
    const before = get().groups;
    set({
      groups: updateStoryInGroups(before, storyId, (s) => ({
        ...s,
        myReaction: reaction,
      })),
    });
    try {
      const updated = await StoryApi.react(storyId, reaction);
      set((s) => ({
        groups: updateStoryInGroups(s.groups, storyId, () => updated),
      }));
      return true;
    } catch {
      set({ groups: before });
      return false;
    }
  },

  replyToStory: async (storyId, text) => {
    try {
      const reply = await StoryApi.reply(storyId, text);
      set((s) => ({
        groups: updateStoryInGroups(s.groups, storyId, (st) => ({
          ...st,
          replies: [...st.replies, reply],
        })),
      }));
      return true;
    } catch (e) {
      set({
        error: e instanceof Error ? e.message : "Failed to send reply",
      });
      return false;
    }
  },
}));